/**
 * Staff Management Phase 4 — Split list by category.
 * Three tabs: Teaching · Administrative · Casual & Volunteers.
 * Each tab is a sortable/filterable/paginated list with category-specific columns.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Users, Search, GraduationCap, Briefcase, HandHeart, AlertTriangle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import ExportButton from '../components/ExportButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StaffManagementPage = () => {
  const [tab, setTab] = useState('teaching');
  const [expiringCount, setExpiringCount] = useState(0);

  useEffect(() => {
    axios.get(`${API_URL}/api/staff-mgmt/certifications/expiring?days=30`)
      .then((r) => setExpiringCount(r.data.items?.length || 0))
      .catch(() => {});
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Users className="h-7 w-7 text-primary" />
          <div>
            <h1 className="text-2xl font-heading font-bold">Staff Management</h1>
            <p className="text-sm text-muted-foreground">Teaching · Administrative · Casual & Volunteers</p>
          </div>
        </div>
        {expiringCount > 0 && (
          <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200 px-3 py-1.5">
            <AlertTriangle className="h-3.5 w-3.5 mr-1 inline" /> {expiringCount} certification{expiringCount !== 1 && 's'} expiring within 30 days
          </Badge>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-3 w-full md:w-auto">
          <TabsTrigger value="teaching" data-testid="staff-tab-teaching"><GraduationCap className="h-3.5 w-3.5 mr-1" />Teaching</TabsTrigger>
          <TabsTrigger value="administrative" data-testid="staff-tab-admin"><Briefcase className="h-3.5 w-3.5 mr-1" />Administrative</TabsTrigger>
          <TabsTrigger value="casual_volunteer" data-testid="staff-tab-casual"><HandHeart className="h-3.5 w-3.5 mr-1" />Casual & Volunteers</TabsTrigger>
        </TabsList>

        <TabsContent value="teaching" className="mt-4"><StaffList category="teaching" /></TabsContent>
        <TabsContent value="administrative" className="mt-4"><StaffList category="administrative" /></TabsContent>
        <TabsContent value="casual_volunteer" className="mt-4"><StaffList category="casual_volunteer" /></TabsContent>
      </Tabs>
    </div>
  );
};

const StaffList = ({ category }) => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { category, limit: 100 };
      if (search) params.search = search;
      if (status !== 'all') params.employment_status = status;
      const res = await axios.get(`${API_URL}/api/staff-mgmt/list`, { params });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (e) { toast.error('Failed to load staff'); }
    finally { setLoading(false); }
  }, [category, search, status]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const renderCategorySpecific = (s) => {
    if (category === 'teaching') {
      return (
        <>
          <TableCell className="text-sm">{(s.subjects || []).join(', ') || '—'}</TableCell>
          <TableCell className="text-xs">{(s.grade_levels || []).join(', ') || '—'}</TableCell>
          <TableCell className="text-xs">{s.license_number ? <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Licensed</Badge> : <span className="text-muted-foreground">—</span>}</TableCell>
        </>
      );
    }
    if (category === 'administrative') {
      return (
        <>
          <TableCell className="text-sm">{s.department || '—'}</TableCell>
          <TableCell className="text-sm">{s.job_title || '—'}</TableCell>
          <TableCell className="text-xs">{s.work_location || '—'}</TableCell>
        </>
      );
    }
    // casual_volunteer
    return (
      <>
        <TableCell className="text-xs">{s.contract_period_from || '—'} → {s.contract_period_to || '—'}</TableCell>
        <TableCell>
          {s.background_check_status === 'cleared' ? <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Cleared</Badge>
            : s.background_check_status === 'pending' ? <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">Pending</Badge>
            : s.background_check_status === 'flagged' ? <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Flagged</Badge>
            : <span className="text-muted-foreground text-xs">—</span>}
        </TableCell>
        <TableCell className="text-xs">{s.availability_schedule || '—'}</TableCell>
      </>
    );
  };

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0 flex-wrap gap-2">
        <CardTitle className="text-base">{total} staff member{total !== 1 && 's'}</CardTitle>
        <div className="flex gap-2 flex-wrap">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 w-56" data-testid={`staff-${category}-search`} />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="on_leave">On leave</SelectItem>
              <SelectItem value="terminated">Terminated</SelectItem>
            </SelectContent>
          </Select>
          <ExportButton resource="staff" label="Export" />
        </div>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Phone</TableHead>
              {category === 'teaching' && <><TableHead>Subjects</TableHead><TableHead>Grades</TableHead><TableHead>License</TableHead></>}
              {category === 'administrative' && <><TableHead>Department</TableHead><TableHead>Title</TableHead><TableHead>Location</TableHead></>}
              {category === 'casual_volunteer' && <><TableHead>Contract Period</TableHead><TableHead>Background Check</TableHead><TableHead>Availability</TableHead></>}
              <TableHead className="text-center">Certs</TableHead>
              <TableHead>Status</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={8} className="text-center py-6 text-muted-foreground">Loading…</TableCell></TableRow>
            : items.length === 0 ? <TableRow><TableCell colSpan={8} className="text-center py-6 text-muted-foreground">No staff in this category</TableCell></TableRow>
            : items.map((s) => {
              const fullName = s.name || `${s.first_name || ''} ${s.last_name || ''}`.trim();
              const w = s.cert_warnings || {};
              return (
                <TableRow key={s.id} className="cursor-pointer hover:bg-muted/30" onClick={() => navigate(`/staff/${s.id}`)} data-testid={`staff-row-${s.id}`}>
                  <TableCell className="font-medium">{fullName}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{s.phone || '—'}</TableCell>
                  {renderCategorySpecific(s)}
                  <TableCell className="text-center">
                    {w.expired > 0 && <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 mr-1"><AlertTriangle className="h-3 w-3 inline mr-1" />{w.expired}</Badge>}
                    {w.expiring_soon > 0 && <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200"><Clock className="h-3 w-3 inline mr-1" />{w.expiring_soon}</Badge>}
                    {!w.expired && !w.expiring_soon && <span className="text-muted-foreground text-xs">—</span>}
                  </TableCell>
                  <TableCell>
                    {(s.is_active !== false && (s.employment_status || 'active') === 'active') ? <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Active</Badge>
                      : <Badge variant="outline" className="bg-gray-100 text-gray-700">{s.employment_status || 'inactive'}</Badge>}
                  </TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <Button size="sm" variant="ghost" onClick={() => navigate(`/staff/${s.id}`)} data-testid={`staff-open-${s.id}`}>Open →</Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default StaffManagementPage;
